<template>
  <div v-if="visible">
    <template v-if="sectionTitle">
      <el-divider class="my-16" />
      <div class="section-header">
        <span class="title">{{ sectionTitle }}</span>
      </div>
    </template>

    <el-skeleton :loading="loading" animated>
      <div slot="template" class="d-flex justify-content-center">
        <el-skeleton-item class="loading-profile-link mr-8" variant="circle" />
        <el-skeleton-item class="loading-profile-link mr-8" variant="circle" />
        <el-skeleton-item class="loading-profile-link mr-8" variant="circle" />
        <el-skeleton-item class="loading-profile-link" variant="circle" />
      </div>

      <ul class="profile-links">
        <div v-if="candidate.resumeFileUrl" class="d-flex align-items-center clickable">
          <el-tooltip :content="`${candidate.name} resume`" :open-delay="250">
            <a class="mr-8" :href="candidate.resumeFileUrl" target="_blank">
              <img :src="require('@/assets/images/candidate-card/resume-download.svg')" />
            </a>
          </el-tooltip>
        </div>

        <div class="d-flex align-items-center">
          <ProfileEmail
            v-if="showEmailCopy"
            class="mr-8"
            :email="candidate.email"
          />
        </div>

        <div class="d-flex align-items-center">
          <ProfilePhone
            v-if="candidate.phone"
            class="mr-8"
            :phone="candidate.phone"
          />
        </div>

        <li v-for="link in displayLinks" :key="link.value">
          <a
            :href="link.value"
            :class="link.type"
            class="profile-link"
            target="_blank"
            rel="noreferrer"
          >
            <el-tooltip
              v-if="link.type === 'edit'"
              class="d-flex"
              content="Edit"
              :open-delay="500"
            >
              <i class="fas fa-pen-square"></i>
            </el-tooltip>

            <el-tooltip
              v-else
              class="d-flex"
              :content="link.title"
              :open-delay="500"
            >
              <span>
                <img :src="require(`@/assets/images/candidate-card/${linkImageName(link)}`)" />
              </span>
            </el-tooltip>
          </a>
        </li>

        <li v-if="collapseSecondary && allowedLinks.length > 0">
          <el-tooltip :content="allowedLinksTooltip">
            <span class="profile-link secondary-collapsed">
              +{{ allowedLinks.length }}
            </span>
          </el-tooltip>
        </li>
      </ul>
    </el-skeleton>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import ProfileEmail from "@/components/ProfileEmail";
import ProfilePhone from "@/components/ProfilePhone";

export default {
  name: "CandidateProfileLinks",
  components: {
    ProfileEmail,
    ProfilePhone,
  },
  props: {
    candidate: {
      type: Object,
      required: true,
    },
    skip: {
      type: Array,
      default: () => {
        return [];
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    linkTypes: {
      type: String,
      default: "primary", // 'primary', 'secondary', or 'all'
      validator: (value) => ["primary", "secondary", "all"].includes(value),
    },
    collapseSecondary: {
      type: Boolean,
      default: false,
    },
    sectionTitle: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      justCopied: {
        phone: false,
        email: false,
      },
    };
  },
  computed: {
    ...mapGetters([
      "currentTeam",
    ]),
    showEmailCopy() {
      return this.candidate.email && !this.candidate.email.startsWith("––@");
    },
    combinedLinks() {
      const links = [];
      const seenUrls = new Set();

      // Add candidate.urls (already in correct format)
      if (this.candidate.urls) {
        this.candidate.urls.forEach(link => {
          if (link.value && !seenUrls.has(link.value)) {
            links.push(link);
            seenUrls.add(link.value);
          }
        });
      }

      // Add candidate.socialLinks (map from { network: url } to { type, value, title })
      if (this.candidate.socialLinks) {
        Object.entries(this.candidate.socialLinks).forEach(([network, url]) => {
          if (url && !seenUrls.has(url)) {
            links.push({
              type: network,
              value: url,
              title: this.formatNetworkName(network),
            });
            seenUrls.add(url);
          }
        });
      }

      // Add candidate.websites (map from { url } to { type, value, title })
      if (this.candidate.websites) {
        this.candidate.websites.forEach(website => {
          if (website.url && !seenUrls.has(website.url)) {
            const detectedType = this.detectTypeFromUrl(website.url);
            const title = website.title || (detectedType === "custom" ? this.extractHostname(website.url) : this.formatNetworkName(detectedType));
            links.push({
              type: detectedType,
              value: website.url,
              title: title,
            });
            seenUrls.add(website.url);
          }
        });
      }

      return links;
    },
    allowedLinks() {
      return this.combinedLinks.filter(l => {
        if (this.skip.includes(l.type)) {
          return false;
        }

        if (this.linkTypes === "all") {
          return true;
        }

        const isPrimaryLink = this.isPrimaryLinkType(l.type);
        return this.linkTypes === "primary" ? isPrimaryLink : !isPrimaryLink;
      });
    },
    displayLinks() {
      if (this.collapseSecondary && this.linkTypes === "secondary") {
        return [];
      }
      return this.allowedLinks;
    },
    allowedLinksTooltip() {
      return this.allowedLinks.map(link => link.title).join(", ");
    },
    visible() {
      const hasLinks = this.displayLinks.length > 0 || (this.collapseSecondary && this.allowedLinks.length > 0);
      const hasPrimaryContent = (
        (this.linkTypes === "primary" || this.linkTypes === "all") &&
        (this.candidate.resumeFileUrl || this.showEmailCopy || this.candidate.phone)
      );

      return hasLinks || hasPrimaryContent;
    },
  },
  methods: {
    formatNetworkName(network) {
      const names = {
        aboutme: "About.me",
        angellist: "AngelList",
        behance: "Behance",
        crunchbase: "Crunchbase",
        dribbble: "Dribbble",
        ello: "Ello",
        facebook: "Facebook",
        flickr: "Flickr",
        foursquare: "Foursquare",
        github: "GitHub",
        gitlab: "GitLab",
        google: "Google",
        gravatar: "Gravatar",
        indeed: "Indeed",
        instagram: "Instagram",
        klout: "Klout",
        linkedin: "LinkedIn",
        medium: "Medium",
        meetup: "Meetup",
        myspace: "MySpace",
        pinterest: "Pinterest",
        quora: "Quora",
        reddit: "Reddit",
        soundcloud: "SoundCloud",
        stackoverflow: "Stack Overflow",
        twitter: "Twitter/X",
        vimeo: "Vimeo",
        wordpress: "WordPress",
        xing: "Xing",
        youtube: "YouTube",
      };
      return names[network] || network.charAt(0).toUpperCase() + network.slice(1);
    },
    detectTypeFromUrl(url) {
      const urlLower = url.toLowerCase();

      // Check for known social networks/platforms
      const typePatterns = {
        linkedin: ["linkedin.com"],
        github: ["github.com"],
        facebook: ["facebook.com", "fb.com"],
        twitter: ["twitter.com", "x.com"],
        instagram: ["instagram.com"],
        youtube: ["youtube.com", "youtu.be"],
        medium: ["medium.com"],
        stackoverflow: ["stackoverflow.com"],
        reddit: ["reddit.com"],
        behance: ["behance.net"],
        dribbble: ["dribbble.com"],
        pinterest: ["pinterest.com"],
        angellist: ["angel.co", "angellist.com"],
        crunchbase: ["crunchbase.com"],
        producthunt: ["producthunt.com"],
        quora: ["quora.com"],
        soundcloud: ["soundcloud.com"],
        vimeo: ["vimeo.com"],
        flickr: ["flickr.com"],
        gitlab: ["gitlab.com"],
        aboutme: ["about.me"],
        wordpress: ["wordpress.com"],
        xing: ["xing.com"],
        foursquare: ["foursquare.com"],
        gravatar: ["gravatar.com"],
        klout: ["klout.com"],
        meetup: ["meetup.com"],
        myspace: ["myspace.com"],
        ello: ["ello.co"],
        indeed: ["indeed.com"],
      };

      for (const [type, patterns] of Object.entries(typePatterns)) {
        if (patterns.some(pattern => urlLower.includes(pattern))) {
          return type;
        }
      }

      return "custom";
    },
    extractHostname(url) {
      try {
        const urlObj = new URL(url.startsWith("http") ? url : `https://${url}`);
        return urlObj.hostname.replace(/^www\./, "");
      } catch {
        return url;
      }
    },
    isPrimaryLinkType(type) {
      const primarySocialNetworks = [
        // Primary social networks
        "linkedin", "github", "facebook", "twitter",
        // ATS/System links
        "topfunnel", "greenhouse", "greenhouse_web", "lever", "smart_recruiters",
        "career_builder", "gr8_people", "mailbox", "google", "ashby", "bamboohr",
        "chat", "fountain", "icims", "jazzhr", "jobvite", "oracle", "sap",
        "taleo", "workable", "workday",
      ];

      return primarySocialNetworks.includes(type);
    },
    linkImageName(link) {
      switch (link.type) {
        case "topfunnel":
          return "humanly.svg";
        case "greenhouse":
        case "greenhouse_web":
          return "greenhouse.svg";
        case "lever":
          return "lever.svg";
        case "smart_recruiters":
          return "smartrecruiters.svg";
        case "career_builder":
          return "career-builder.svg";
        case "gr8_people":
          return "gr8-people.svg";
        case "mailbox":
          return "gmail.svg";
        case "google":
          return "google.svg";
        case "github":
          return "github.svg";
        case "linkedin":
          return "linkedin.svg";
        case "ashby":
          return "ashby.svg";
        case "bamboohr":
          return "bamboohr.svg";
        case "chat":
          return "chat.svg";
        case "fountain":
          return "fountain.svg";
        case "icims":
          return "icims.svg";
        case "jazzhr":
          return "jazzhr.svg";
        case "jobvite":
          return "jobvite.svg";
        case "oracle":
          return "oracle.svg";
        case "sap":
          return "sap.svg";
        case "taleo":
          return "taleo.svg";
        case "workable":
          return "workable.svg";
        case "workday":
          return "workday.svg";
        // Social networks
        case "angellist":
          return "angellist.svg";
        case "behance":
          return "behance.svg";
        case "crunchbase":
          return "crunchbase.svg";
        case "dribbble":
          return "dribbble.svg";
        case "facebook":
          return "facebook.svg";
        case "instagram":
          return "instagram.svg";
        case "medium":
          return "medium.svg";
        case "pinterest":
          return "pinterest.svg";
        case "producthunt":
          return "producthunt.svg";
        case "quora":
          return "quora.svg";
        case "reddit":
          return "reddit.svg";
        case "stackoverflow":
          return "stackoverflow.svg";
        case "twitter":
          return "twitter.svg";
        case "youtube":
          return "youtube.svg";
        default:
          return "custom.svg";
      }
    },
    copyTextToClipboard(text, type) {
      this.justCopied[type] = true;
      navigator.clipboard.writeText(text).then(() => {
        setTimeout(() => this.justCopied[type] = false, 3000);
      });
    },
  },
};
</script>

<style scoped lang="scss">
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 0 16px;

  .title {
    font-size: 14px;
    font-weight: 600;
    color: #606266;
  }
}

.loading-profile-link {
  width: 24px;
  height: 24px;
}

.profile-links {
  margin-bottom: 0;
  padding: 0;
  display: flex;
  font-size: $tf-icon-size;
  transition: $tf-global-transition;
  cursor: pointer;
  list-style-type: none;

  li {
    float: left;
    margin-right: 8px;
    display: flex;
    align-items: center;

    &:last-child {
      margin-right: 0;
    }
  }

  img {
    height: 24px;
    border-radius: 100%;
  }

  a {
    display: flex;
    color: gray;
    transition: $tf-global-transition;
    text-decoration: none;

    &:hover {
      opacity: 0.75;
    }
  }

  .secondary-collapsed {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 100%;
    background-color: #f5f5f5;
    color: gray;
    font-size: 12px;
    font-weight: 500;
    cursor: help;

    &:hover {
      opacity: 0.75;
    }
  }
}
</style>
